import {fetchWithAuth} from '@shared/utils/auth';
import {apiEndpoints, requestOptions} from './api/calendarApi';

const CalendarSystem = {
  namespaced: true,

  state: {
    isLoading: false,
    isReady: false,
    isLoadingEvent: false,
    errors: [],
    currentMonth: null,
    currentMonthOffset: 0,
    minMonthOffset: null,
    maxMonthOffset: null,
    calendarData: [],
    activeFilter: null,
    filteredCalendarData: [], // Для хранения данных после фильтрации
    calendarEventData: [],
    activeTooltipId: null,
  },

  getters: {
    getCurrentMonth(state) {
      return state.currentMonth;
    },
    getCurrentMonthOffset(state) {
      return state.currentMonthOffset;
    },
    getMinMonthOffset(state) {
      return state.minMonthOffset;
    },
    getMaxMonthOffset(state) {
      return state.maxMonthOffset;
    },
    getCalendarData(state) {
      return state.calendarData;
    },
    getIsLoading(state) {
      return state.isLoading;
    },
    getIsLoadingEvent(state) {
      return state.isLoadingEvent;
    },
    getCalendarEventData(state) {
      return state.calendarEventData
    },
    getActiveTooltipId(state) {
      return state.activeTooltipId
    },
    getReadyStatus(state) {
      return state.isReady
    },
  },

  mutations: {
    setCurrentMonthOffset(state, offset) {
      state.currentMonthOffset = offset;
    },
    setMinMonthOffset(state, offset) {
      state.minMonthOffset = offset;
    },
    setMaxMonthOffset(state, offset) {
      state.maxMonthOffset = offset;
    },
    setLoading(state, status) {
      state.isLoading = status;
    },
    setLoadingEvent(state, status) {
      state.isLoadingEvent = status
    },
    setErrors(state, error) {
      state.errors.push(error);
    },
    setCurrentMonth(state, month) {
      state.currentMonth = month;
    },
    setCalendarData(state, data) {
      state.calendarData = data;
    },
    setActiveFilter(state, filter) {
      state.activeFilter = filter;
    },
    setCalendarEventData(state, data) {
      state.calendarEventData = data;
    },
    setActiveTooltipId(state, id) {
      state.activeTooltipId = id;
    },
    setReadyStatus(state, status) {
      state.isReady = status;
    }
  },

  actions: {
    async fetchCalendarData({commit, state}, monthOffset) {
      try {
        commit('setLoading', true);
        const url = `${apiEndpoints.calendarMonthly}`;
        const response = await fetchWithAuth(
          url,
          requestOptions.post({month_offset: monthOffset})
        );
        if (!response.ok) {
          throw new Error(`Network error: ${response.status} - ${response.statusText}`);
        }

        const data = await response.json();

        commit('setCurrentMonthOffset', monthOffset);
        commit('setReadyStatus', true);
        commit('setCurrentMonth', data.display_month);
        commit('setCalendarData', data.days);
        commit('setMinMonthOffset', data.min_offset);
        commit('setMaxMonthOffset', data.max_offset);
      } catch (error) {
        commit('setErrors', error.message);
        throw error;
      } finally {
        commit('setLoading', false);
      }
    },

    async fetchEventCalendarData({commit}, {id}) {
      commit('setLoadingEvent', true);
      try {
        const response = await fetchWithAuth(`${apiEndpoints.calendarEvent}${id}`, requestOptions.post);

        if (!response.ok) {
          throw new Error(`Network error: ${response.status} - ${response.statusText}`);
        }

        const data = await response.json();
        commit('setCalendarEventData', data);
      } catch (error) {
        commit('setErrors', error.message);
      } finally {
        commit('setLoadingEvent', false);
      }
    },

    async fetchFilteredCalendarData({commit}, filterParam) {
      try {
        commit('setLoading', true);
        const url = `${apiEndpoints.calendar}filter/?highlighted=${filterParam}`;
        const response = await fetchWithAuth(url, requestOptions.post);

        if (!response.ok) {
          throw new Error(`Network error: ${response.status} - ${response.statusText}`);
        }

        const data = await response.json();
        commit('setCurrentMonth', data.month);
        commit('setCalendarData', data.days);
      } catch (error) {
        commit('setErrors', error.message);
      } finally {
        commit('setLoading', false);
      }
    },

    async loadCalendarData({dispatch, commit}, monthOffset) {
      await dispatch('fetchCalendarData', monthOffset);
    },

    async loadCalendarEventData({dispatch}, id) {
      await dispatch('fetchEventCalendarData', id);
    },

    async applyFilter({dispatch, commit}, filterParam) {
      if (filterParam) {
        commit('setActiveFilter', filterParam);
        await dispatch('fetchFilteredCalendarData', filterParam);
      } else {
        commit('setActiveFilter', null);
        await dispatch('loadCalendarData');
      }
    },
  },
};

export default CalendarSystem;
